import React, { useEffect, useRef, useState } from 'react'
// eslint-disable-next-line import/no-extraneous-dependencies
import * as billboard from 'billboard.js'
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import BillboardChart from 'react-billboardjs'
import '@Ui/css/billboard.css'
import className from 'classnames/bind'
import { delay, isEmpty } from 'lodash'
import { format } from 'date-fns'

import style from './MyhouseTrendPriceChart.module.scss'

import { PRICE_PROVIDER, PROVIDER } from '@Root/contants'
import MarketPriceChart from '@Types/entities/MarketPriceChart'
import SVG_MyhouseTrendChartArrow from '@Ui/images/myhouse/myhouse_trend_chart--arrow.svg'
import { formatMoney, MONEY_FORMAT_TYPE } from '@Root/utils/moneyFormatter'

const cx = className.bind(style)

type Props = {
  chart: MarketPriceChart
}

const parseChartData = (chart: MarketPriceChart, handleClick: (d: any, element: any) => void, handleOut: (d: any, element: any) => void) => {
  const axisXCount = Math.max(chart.marketPriceXList.length, chart.realPriceDataXList.length)

  return {
    padding: {
      left: 50,
      right: 25,
    },
    size: {
      height: 200,
    },
    line: {
      point: [chart.realPriceDataYList[0]],
    },
    data: {
      xs: {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        [chart.realPriceDataYList[0]]: chart.realPriceDataXList[0],
        ump_y: chart.marketPriceXList[0],
        lmp_y: chart.marketPriceXList[0],
      },
      columns: [chart.realPriceDataYList, chart.realPriceDataXList, chart.marketPriceXList, chart.upperMarketPriceYList, chart.lowerMarketPriceYList],
      types: {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        [chart.realPriceDataYList[0]]: 'scatter',
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        [chart.upperMarketPriceYList[0]]: 'area-line-range',
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        [chart.lowerMarketPriceYList[0]]: 'line',
      },
      colors: {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        [chart.realPriceDataYList[0]]: '#00c73c',
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        [chart.upperMarketPriceYList[0]]: '#419aff',
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        [chart.lowerMarketPriceYList[0]]: '#419aff',
      },
      onclick: handleClick,
      onout: handleOut,
    },
    axis: {
      x: {
        tick: {
          outer: false,
          format: (datetime: string) => format(new Date(datetime), 'yy.MM'),
          count: chart.year >= 3 ? Math.min(axisXCount, 6) : Math.min(axisXCount, 4),
        },
        type: 'timeseries',
      },
      y: {
        tick: {
          fit: false,
          outer: false,
          values: chart.axisYTickValues,
          format: (y: number) => formatMoney(y, MONEY_FORMAT_TYPE.MUCH, 1),
        },
        min: chart.minAxisY,
        max: chart.maxAxisY,
      },
    },
    legend: {
      show: false,
    },
    grid: {
      y: {
        lines: chart.gridYLines,
      },
      lines: {
        front: false,
      },
    },
    tooltip: {
      show: false,
    },
  }
}

const MyhouseTrendPriceChart: React.FC<Props> = ({ chart }: Props) => {
  const chartContainerRef = useRef<BillboardChart>(null)
  const tooltipContainerRef = useRef<HTMLDivElement>(null)
  const [isChartDraw, setIsChartDraw] = useState(false)
  const [chartData, setChartData] = useState({})
  const [tooltipAttribute, setTooltipAttribute] = useState({
    className: [] as string[],
    style: {
      display: 'none',
    } as any,
    floor: '',
    money: '',
  })

  billboard.line()
  billboard.areaLineRange()

  const handleOut = () => {
    setTooltipAttribute({
      ...tooltipAttribute,
      className: [],
      style: {
        display: 'none',
      },
    })
  }

  const handleClick = (d: any, element: any) => {
    const chartElement = chartContainerRef.current.chartElement
    const tooltipContainer = tooltipContainerRef.current

    if (!chartElement || !tooltipContainer) {
      return
    }

    if (d && d.id === 'rp_y') {
      const eventPoint = {
        x: parseInt(element.getAttribute('cx'), 10),
        y: parseInt(element.getAttribute('cy'), 10),
      }

      setTooltipAttribute({
        className: [],
        style: {
          display: 'block',
        },
        floor: String(chart.floorList[d.index]),
        money: formatMoney(d.value, MONEY_FORMAT_TYPE.LITTLE),
      })

      const tooltipRect = tooltipContainer.getBoundingClientRect()
      const chartRect = chartElement.getBoundingClientRect()
      const eventAreaRect = chartElement.getElementsByClassName('bb-event-rect')[0].getBoundingClientRect()
      const eventAreaRectLeftPadding = eventAreaRect.left - chartRect.left

      const top = eventPoint.y - tooltipRect.height + chartElement.offsetTop
      const left = eventPoint.x + eventAreaRectLeftPadding - tooltipRect.width / 2 + chartElement.offsetLeft

      if (left + tooltipRect.width >= chartRect.right) {
        // type_right 추가시 left:50% -> right: 13px, transform:none;
        const fixedLeft = left - tooltipRect.width / 2 + 13 + 3
        setTooltipAttribute({
          ...tooltipAttribute,
          className: ['type_right'],
          style: {
            display: 'block',
            top: `${top}px`,
            left: `${fixedLeft}px`,
          },
          floor: String(chart.floorList[d.index]),
          money: formatMoney(d.value, MONEY_FORMAT_TYPE.LITTLE),
        })
      } else {
        tooltipAttribute.className = []
        setTooltipAttribute({
          className: [],
          style: {
            display: 'block',
            top: `${top}px`,
            left: `${left}px`,
          },
          floor: String(chart.floorList[d.index]),
          money: formatMoney(d.value, MONEY_FORMAT_TYPE.LITTLE),
        })
      }
    }
  }

  useEffect(() => {
    setIsChartDraw(false)
    handleOut()
    if (!isEmpty(chart) && chart.exposeChart) {
      const chartData = parseChartData(chart, handleClick, handleOut)
      setChartData(chartData)

      delay(() => {
        setIsChartDraw(true)
      }, 50)
    }
  }, [chart])

  const getProviderLabel = (provider: string): string => {
    if (provider) {
      return PRICE_PROVIDER[provider as PROVIDER].LABEL
    }
    return ''
  }

  const getBasisDateString = (basisYMD: string): string => {
    if (basisYMD) {
      return [basisYMD.slice(0, 4), basisYMD.slice(4, 6), basisYMD.slice(6, 8)].join('.')
    }
    return ''
  }

  return (
    <div className={cx('myhouse_price_chart')}>
      <div className={cx('myhouse_chart_header')}>
        <div className={cx('myhouse_element_list')}>
          <span className={cx('myhouse_chart_element', 'type_price')}>시세</span>
          <span className={cx('myhouse_chart_element', 'type_transaction')}>실거래가</span>
        </div>
        {!!chart.chartProvider && (
          <em className={cx('myhouse_chart_standard')}>{`${getBasisDateString(chart.marketPriceChartBasisYearMonthDay)}. ${getProviderLabel(chart.chartProvider)} 기준`}</em>
        )}
      </div>
      <div style={{ height: '200px' }}>
        {isChartDraw && (
          <>
            <BillboardChart {...chartData} className={cx('myhouse_chart', 'type_price')} ref={chartContainerRef} />
            {/* 층 수/실거래가 정보 클릭 시 innerHTML로 삽입, absolute 로 위치적용 */}
            {/* 차트 밖으로 나가는 경우 type_right 클래스 추가 */}
            <div
              className={cx('myhouse_chart_tooltip', 'type_price', tooltipAttribute.className)}
              style={tooltipAttribute.style}
              ref={tooltipContainerRef}
              onMouseLeave={handleOut}
              onPointerLeave={handleOut}
            >
              <span className={cx('myhouse_tooltip_text')}>
                {tooltipAttribute.floor}층<strong className={cx('myhouse_tooltip_emphasis')}>{tooltipAttribute.money}</strong>
              </span>
              <SVG_MyhouseTrendChartArrow />
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default MyhouseTrendPriceChart
